import type { RecipeSummary } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { MacroStrip } from '@/components/ui/MacroStrip'
import { PreviewButton } from '@/components/ui/PreviewButton'
import { RecipeCover } from '@/components/ui/RecipeCover'
import { RecipeFacts } from '@/features/recipes/RecipeFacts'
import { cn } from '@/lib/cn'
import { formatScore } from '@/lib/format'
import { addMacros, summaryMacros } from '@/lib/macros'
import { AlertTriangle, Check, ChevronDown, CircleCheck, Plus, RefreshCw, X } from 'lucide-react'
import { forwardRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ScoreBreakdown } from './ScoreBreakdown'
import type { ScoreRow } from './scoreRows'

export interface SlotSearch {
  options: ComboboxOption[]
  onSearch: (term: string) => void
  onPick: (id: number) => void
  loading: boolean
}

export interface FilledSlot {
  id: number
  name: string
  /** Missing while the recommendation payload for it is still loading. */
  recipe: RecipeSummary | undefined
  score?: { value: number; rows: ScoreRow[]; label: string }
}

export interface TrayProps {
  meal1: FilledSlot | null
  meal2: FilledSlot | null
  search1: SlotSearch
  search2: SlotSearch
  onPreview: (id: number) => void
  onChangeMeal1: () => void
  onRemoveMeal1: () => void
  onRemoveMeal2: () => void
  pairScore?: { value: number; rows: ScoreRow[] }
  /** Rule checks: null while unknown, otherwise the broken rules, described. */
  warnings: string[] | null
  chosen: string | null
  onCookOne: () => void
  onCookBoth: () => void
}

/** The two picks side by side, with their combined numbers and the cook actions. */
export const SelectionTray = forwardRef<HTMLElement, TrayProps>(function SelectionTray(
  props,
  ref,
) {
  const {
    meal1,
    meal2,
    search1,
    search2,
    onPreview,
    onChangeMeal1,
    onRemoveMeal1,
    onRemoveMeal2,
    pairScore,
    warnings,
    chosen,
    onCookOne,
    onCookBoth,
  } = props
  const combined =
    meal1?.recipe && meal2?.recipe
      ? addMacros(summaryMacros(meal1.recipe), summaryMacros(meal2.recipe))
      : null

  return (
    <section
      ref={ref}
      aria-label="Your picks"
      className="rounded-[var(--radius-card)] border border-line/70 bg-surface shadow-card"
    >
      <div className="grid gap-3 p-3 md:grid-cols-2">
        {meal1 ? (
          <Slot
            label="Meal 1"
            slot={meal1}
            onPreview={() => onPreview(meal1.id)}
            onRemove={onRemoveMeal1}
            extra={
              <Button variant="ghost" size="sm" onClick={onChangeMeal1}>
                <RefreshCw size={14} aria-hidden />
                Change
              </Button>
            }
          />
        ) : (
          <EmptySlot label="Meal 1" search={search1} hint="Pick from the suggestions below, or search." />
        )}
        {meal2 ? (
          <Slot
            label="Meal 2"
            slot={meal2}
            onPreview={() => onPreview(meal2.id)}
            onRemove={onRemoveMeal2}
          />
        ) : (
          <EmptySlot
            label="Meal 2"
            search={search2}
            disabled={!meal1}
            hint={meal1 ? 'Pick a partner below, or search.' : 'Optional. Choose meal 1 first.'}
          />
        )}
      </div>

      {meal1 ? (
        <div className="flex flex-col gap-3 border-t border-line px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
            {pairScore ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-ink-faint">Pair score</span>
                <span className="tabular ml-1 text-lg font-semibold text-ink">
                  {formatScore(pairScore.value)}
                </span>
                <ScoreBreakdown rows={pairScore.rows} total={pairScore.value} totalLabel="Pair score" />
              </div>
            ) : null}
            {combined ? (
              <div className="min-w-0">
                <p className="text-xs text-ink-faint">Both, one serving each</p>
                <MacroStrip macros={combined} />
              </div>
            ) : null}
            <RuleStatus warnings={warnings} pair={meal2 !== null} />
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant={meal2 ? 'secondary' : 'primary'}
              loading={chosen === 'meal-1'}
              onClick={onCookOne}
            >
              {meal2 ? 'Cook meal 1 only' : 'Cook meal 1'}
            </Button>
            {meal2 ? (
              <Button variant="primary" loading={chosen === 'both'} onClick={onCookBoth}>
                <Check size={16} aria-hidden />
                Cook both
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
})

function RuleStatus({ warnings, pair }: { warnings: string[] | null; pair: boolean }) {
  if (warnings === null) return null
  if (warnings.length === 0) {
    return (
      <p className="inline-flex items-center gap-1.5 text-sm text-positive">
        <CircleCheck size={16} aria-hidden />
        {pair ? 'Pair matches all your rules' : 'Matches all your rules'}
      </p>
    )
  }
  return (
    <details className="group relative text-sm">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-caution">
        <AlertTriangle size={16} aria-hidden />
        Breaks {warnings.length === 1 ? '1 rule' : `${warnings.length} rules`}
        <ChevronDown size={14} aria-hidden className="transition-transform group-open:rotate-180" />
      </summary>
      <ul className="mt-1.5 space-y-0.5 rounded-[var(--radius-field)] bg-caution-soft px-3 py-2 text-caution">
        {warnings.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </details>
  )
}

function Slot({
  label,
  slot,
  onPreview,
  onRemove,
  extra,
}: {
  label: string
  slot: FilledSlot
  onPreview: () => void
  onRemove: () => void
  extra?: ReactNode
}) {
  const recipe = slot.recipe
  return (
    <div className="flex min-w-0 gap-3 rounded-[var(--radius-field)] bg-surface-sunken/60 p-3">
      <RecipeCover
        imageUrl={recipe?.image_url ?? null}
        cuisine={recipe?.cuisine ?? null}
        iconSize={20}
        className="hidden size-20 shrink-0 rounded-[var(--radius-field)] sm:block"
      />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-accent-ink">{label}</p>
            <Link
              to={`/recipes/${slot.id}`}
              className="line-clamp-2 font-semibold text-ink hover:text-accent"
            >
              {slot.name}
            </Link>
          </div>
          {slot.score ? (
            <div className="flex shrink-0 items-center">
              <span className="tabular text-lg font-semibold text-ink">
                {formatScore(slot.score.value)}
              </span>
              <ScoreBreakdown
                rows={slot.score.rows}
                total={slot.score.value}
                totalLabel={slot.score.label}
              />
            </div>
          ) : null}
        </div>
        {recipe ? (
          <>
            <RecipeFacts recipe={recipe} className="text-xs" />
            <MacroStrip macros={summaryMacros(recipe)} />
          </>
        ) : null}
        <div className="-ml-2.5 flex flex-wrap items-center gap-0.5 pt-0.5">
          <PreviewButton onClick={onPreview} />
          {extra}
          <Button variant="ghost" size="sm" onClick={onRemove}>
            <X size={14} aria-hidden />
            Remove
          </Button>
        </div>
      </div>
    </div>
  )
}

function EmptySlot({
  label,
  search,
  hint,
  disabled = false,
}: {
  label: string
  search: SlotSearch
  hint: string
  disabled?: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col justify-center gap-2 rounded-[var(--radius-field)] border border-dashed border-line-strong p-3',
        disabled ? 'opacity-60' : 'min-h-32',
      )}
    >
      <p className="flex items-center gap-1.5 text-xs font-medium text-ink-faint">
        <Plus size={14} aria-hidden />
        {label}
      </p>
      {disabled ? null : (
        <Combobox
          ariaLabel={`Search for ${label.toLowerCase()}`}
          value={null}
          options={search.options}
          onChange={(value) => {
            if (value !== null) search.onPick(Number(value))
          }}
          onSearch={search.onSearch}
          placeholder="Search any recipe…"
          searchPlaceholder="Search recipes by name…"
          emptyText="No recipe matches that name."
          loading={search.loading}
        />
      )}
      <p className="text-xs text-ink-faint">{hint}</p>
    </div>
  )
}
